import { RadioGroup } from '@affine/component';
import { useI18n } from '@affine/i18n';
import type { FC } from 'react';

import * as css from './controls.css';

/**
 * `labelKey` is an i18n resource key resolved at render time. We keep options
 * as plain data (no React hooks at module-init) and translate inside the
 * EnumSelect render — keeps section files lean and avoids re-implementing the
 * translation step at every callsite.
 */
export interface EnumOption<T extends string> {
  value: T;
  labelKey: string;
  description?: string;
}

export interface EnumSelectProps<T extends string> {
  value: T;
  options: ReadonlyArray<EnumOption<T>>;
  onChange: (v: T) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  'data-sl-field': string;
}

const RADIO_MAX_OPTIONS = 4;

export function EnumSelect<T extends string>({
  value,
  options,
  onChange,
  label,
  description,
  disabled,
  'data-sl-field': field,
}: EnumSelectProps<T>): ReturnType<FC> {
  const t = useI18n();
  const useRadio = options.length <= RADIO_MAX_OPTIONS;

  return (
    <div className={css.controlRow} data-sl-field={field}>
      <span className={css.controlLabel}>{label}</span>
      {description ? (
        <span className={css.controlDescription}>{description}</span>
      ) : null}
      {useRadio ? (
        <RadioGroup
          value={value}
          onChange={v => onChange(v as T)}
          items={options.map(opt => ({
            value: opt.value,
            label: t[opt.labelKey](),
          }))}
          disabled={disabled}
          width="100%"
        />
      ) : (
        <select
          className={css.select}
          value={value}
          onChange={e => onChange(e.target.value as T)}
          disabled={disabled}
          data-sl-control="enum-select"
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {t[opt.labelKey]()}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
